import type {ShiftBreak} from "@/src/modules/shifts/types";

export type BreakNoticeVariant = "due" | "dueNoDelay" | "resolveRequired" | "pastShiftEnd";

type BreakNoticeCopy = {
  title: string;
  description: string;
  body: string;
  acknowledgeLabel: string | null;
};

function breakLabel(breakType: ShiftBreak["break_type"]) {
  return breakType === "rest" ? "rest break" : "meal break";
}

/**
 * Copy variant from existing modal flags only (no new actions).
 * `pastShiftEnd` uses client `is_take_disabled` (set after break_extends_past_shift_end).
 */
export function getBreakNoticeVariant(options: {
  canReject: boolean;
  canTakeBreak: boolean;
  isDelayAvailable: boolean;
}): BreakNoticeVariant {
  const {canReject, canTakeBreak, isDelayAvailable} = options;

  if (canReject && !canTakeBreak) {
    return "pastShiftEnd";
  }
  if (canReject) {
    return "resolveRequired";
  }
  if (canTakeBreak && !isDelayAvailable) {
    return "dueNoDelay";
  }
  return "due";
}

export function getBreakNoticeCopy(
  breakType: ShiftBreak["break_type"],
  variant: BreakNoticeVariant,
): BreakNoticeCopy {
  const label = breakLabel(breakType);

  switch (variant) {
    case "pastShiftEnd":
      return {
        title: `Cannot take ${label}`,
        description: "There isn't enough time left in your shift.",
        body: `This break can't be started or delayed because it would run past your shift end. If you already took it during your shift, mark Already taken. If you didn't, choose Reject.`,
        acknowledgeLabel: "I understand",
      };
    case "resolveRequired":
      return {
        title: `${capitalize(label)} — action required`,
        description: "You can't delay this break anymore.",
        body: `If you already took it, mark Already taken. If you didn't, choose Reject.`,
        acknowledgeLabel: "I confirm this is accurate",
      };
    case "dueNoDelay":
      return {
        title: `${capitalize(label)} due`,
        description: `Your scheduled ${label} is due now.`,
        body: `You can't delay this break again. Take your break now, or contact your supervisor if you can't.`,
        acknowledgeLabel: null,
      };
    case "due":
    default:
      return {
        title: `${capitalize(label)} due`,
        description: `Your scheduled ${label} is due now.`,
        body: "",
        acknowledgeLabel: null,
      };
  }
}

function capitalize(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
